/**
 * useEscalationsQuery + useEscalationStatsQuery — P-W34 Phase 3
 *
 * TanStack Query hooks für Eskalations-Inbox.
 * Pattern gespiegelt aus useCareRequests.ts.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isSupabaseConfigured } from '@/lib/supabase';
import {
    listEscalationsForPractice,
    getEscalation,
    getEscalationAuditTrail,
    getEscalationStats,
    subscribeToEscalations,
} from '@/services/escalation.service';

export const escalationKeys = {
    all: ['escalations'] as const,
    list: (practiceId?: string) => [...escalationKeys.all, 'list', practiceId].filter(Boolean),
    detail: (id?: string) => [...escalationKeys.all, 'detail', id].filter(Boolean),
    audit: (id?: string) => [...escalationKeys.all, 'audit', id].filter(Boolean),
    stats: (practiceId?: string) => [...escalationKeys.all, 'stats', practiceId].filter(Boolean),
};

/**
 * Inbox-Liste — auto-refetch via realtime subscription auf practice_id-changes.
 */
export function useEscalationsQuery(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const sub = subscribeToEscalations(practiceId, () => {
            void queryClient.invalidateQueries({
                queryKey: escalationKeys.list(practiceId),
            });
            void queryClient.invalidateQueries({
                queryKey: escalationKeys.stats(practiceId),
            });
        });

        return () => sub.unsubscribe();
    }, [practiceId, queryClient]);

    return useQuery({
        queryKey: escalationKeys.list(practiceId),
        queryFn: () => listEscalationsForPractice(practiceId!),
        enabled: !!practiceId,
        staleTime: 15_000,
    });
}

export function useEscalationDetailQuery(escalationId?: string) {
    return useQuery({
        queryKey: escalationKeys.detail(escalationId),
        queryFn: () => getEscalation(escalationId!),
        enabled: !!escalationId,
        staleTime: 10_000,
    });
}

export function useEscalationAuditQuery(escalationId?: string) {
    return useQuery({
        queryKey: escalationKeys.audit(escalationId),
        queryFn: () => getEscalationAuditTrail(escalationId!),
        enabled: !!escalationId,
        staleTime: 10_000,
    });
}

/**
 * Stats — für Sidebar-Badge.
 * Auto-invalidiert durch useEscalationsQuery wenn realtime fires.
 */
export function useEscalationStatsQuery(practiceId?: string) {
    return useQuery({
        queryKey: escalationKeys.stats(practiceId),
        queryFn: () => getEscalationStats(practiceId!),
        enabled: !!practiceId,
        staleTime: 30_000,
    });
}
